import React, { FC, useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { ExternalBanner, InternalBanner } from '@components/Calendar/Calendar.elements';
import EventTemplate from '@components/Event/Event';
import { Icons } from '@components/Icon';
import Newsletter from '@components/Newsletter/Newsletter';
import SearchTemplate from '@components/Search/Search';
import useTranslations from '@hooks/useTranslation';
import ButtonTemplate from '@ui/Button/Button';
import SpinnerTemplate from '@ui/Spinner/Spinner';
import { publicAssets } from '@utils/url.utils';
import FilterTemplate from '../../components/Filter/Filter';
import {
    CONFERENCES_LIMIT_PER_PAGE,
    ROUTES,
    TRAININGS_LIMIT_PER_PAGE,
    getAdsUrl,
    getImageBaseURL,
} from '../../constants';
import { BannerTypes, FilterViewTypes, ITrainingItem } from '../../interfaces/Calendar';
import {
    ConferenceTypes,
    CourseTypes,
    FilterTypesInResponse,
    FilterTypesInUrl,
    IFilterItem,
    IPackageFilterItem,
} from '../../interfaces/Filter';
import { calendarApi } from '../../services/calendar.service';
import { RootState } from '../../store';
import ContentTemplate from '../../ui/Content/Content';
import {
    Banner,
    BorderedPart,
    DesktopFilters,
    Header,
    Item,
    MobileCloseButton,
    MobileFilterButton,
    MobileFilters,
    MobileFiltersTitle,
    MobileResetSection,
    SearchNoResults,
    Title,
    Trainings,
    Wrapper,
} from './Trainings.elements';

export interface TrainingsProps {
    trainings: ITrainingItem[];
    count: number;
    cityFilters?: IFilterItem[];
    monthFilters?: IFilterItem[];
    packageFilters?: IPackageFilterItem;
    categoryFilters?: IFilterItem[];
    personFilters?: IFilterItem[];
    isMyAccount?: boolean;
    trainingFiltersCount?: { [key in CourseTypes]: number };
    conferencesFiltersCount?: { [key in ConferenceTypes]: number };
}

const INTERNAL_BANNER_POSITION = 3;
const EXTERNAL_BANNER_POSITION = 4;

const TrainingsTemplate: FC<TrainingsProps> = ({
    trainings: data,
    count,
    cityFilters,
    monthFilters,
    categoryFilters,
    packageFilters,
    personFilters,
    isMyAccount,
    trainingFiltersCount,
    conferencesFiltersCount,
}) => {
    const { t } = useTranslations();

    const router = useRouter();

    const isLoggedIn = useSelector((state: RootState) => state.ui.isLoggedIn);

    const [trainings, setTrainings] = useState(data);
    const [totalCount, setTotalCount] = useState(count);
    const [isLoading, setIsLoading] = useState(false);
    const [banners, setBanners] = useState([]);
    const [bannerVersion, setBannerVersion] = useState(BannerTypes.DESKTOP);
    const [filterVersion, setFilterVersion] = useState(FilterViewTypes.DESKTOP);
    const [externalBannerScript, setExternalBannerScript] = useState(null);
    const [isOpenFilter, setIsOpenFilter] = useState(false);

    useEffect(() => {
        setTrainings(data);
        setTotalCount(count);
    }, [data]);

    useEffect(() => {
        if (router.pathname.includes(ROUTES.TRAININGS)) {
            calendarApi.getCoursesBanners().then(({ data }) => setBanners(data?.results || []));
        }

        if (router.pathname.includes(ROUTES.CONFERENCES)) {
            calendarApi.getConferencesBanner().then(({ data }) => setBanners(data?.results || []));
        }

        fetch(`${getAdsUrl()}&zoneid=66`)
            .then((response) => response.text())
            .catch(() => {
                return null;
            })
            .then((data) => {
                setExternalBannerScript(
                    data
                        ?.replace(')', '')
                        ?.replace('document.write(', 'document.getElementById("homepageBanner").innerHTML = ')
                );
            });
    }, []);

    useEffect(() => {
        if (window.innerWidth >= 992) {
            setBannerVersion(BannerTypes.TABLET);
            setFilterVersion(FilterViewTypes.DESKTOP);
        }
        if (window.innerWidth < 992) {
            setFilterVersion(FilterViewTypes.MOBILE);
        }
        if (window.innerWidth < 768) {
            setBannerVersion(BannerTypes.MOBILE);
        }
    });

    const showMore = async (pagination: { selected: number }) => {
        router.replace({
            pathname: router.pathname,
            query: {
                ...router.query,
                strana: pagination.selected + 1,
            },
        });
    };

    const getTitle = () => {
        if (isMyAccount) return t('events.trainings.my.title');

        if (
            router.pathname === ROUTES.TRAININGS &&
            router.query[FilterTypesInUrl.TRAINING_TYPE] === CourseTypes.ARCHIVED
        )
            return t('events.trainings.archived.title');

        if (router.pathname === ROUTES.TRAININGS) return t('events.trainings.upcoming.title');

        if (
            router.pathname === ROUTES.CONFERENCES &&
            router.query[FilterTypesInUrl.CONFERENCE_TYPE] !== FilterTypesInUrl.ARCHIVED
        )
            return t('events.conferences.upcoming.title');

        if (
            router.pathname === ROUTES.CONFERENCES &&
            router.query[FilterTypesInUrl.CONFERENCE_TYPE] === FilterTypesInUrl.ARCHIVED
        )
            return t('events.conferences.archived.title');
    };

    const showBanners = router.pathname === ROUTES.TRAININGS || router.pathname === ROUTES.CONFERENCES;
    const hasSomeFilter =
        trainingFiltersCount?.[CourseTypes.ONLINE] > 0 ||
        trainingFiltersCount?.[CourseTypes.ATTENDANCE] > 0 ||
        conferencesFiltersCount?.[ConferenceTypes.ARCHIVED] > 0 ||
        conferencesFiltersCount?.[ConferenceTypes.UPCOMING] > 0 ||
        categoryFilters?.filter((category) => category?.count > 0)?.length > 0 ||
        cityFilters?.filter((city) => city?.count > 0)?.length > 0 ||
        personFilters?.length > 0;

    const hasSelectedFilters =
        router.query[FilterTypesInUrl.TRAINING_TYPE] ||
        router.query[FilterTypesInUrl.CITY] ||
        router.query[FilterTypesInUrl.CATEGORY] ||
        router.query[FilterTypesInUrl.LECTURER] ||
        router.query[FilterTypesInUrl.MONTH] ||
        router.query[FilterTypesInUrl.CONFERENCE_TYPE] ||
        router.query[FilterTypesInUrl.ARCHIVED] ||
        router.query[FilterTypesInUrl.UPCOMING] ||
        router.query[FilterTypesInUrl.PACKAGES];

    const getSearchLabelByPage = () => {
        if (router.asPath?.includes(ROUTES.MY_PV_MATERIALS) || router.asPath?.includes(ROUTES.MY_PV_FAVOURITES)) {
            return t('search.title.trainingsOrConferences');
        }

        if (router.asPath?.includes(ROUTES.CONFERENCES)) {
            return t('search.title.conferences');
        }

        if (router.asPath?.includes(ROUTES.TRAININGS)) {
            return t('search.title.trainings');
        }
    };

    return (
        <ContentTemplate
            withPadding={!router.pathname?.includes(ROUTES.MY_PV)}
            mobileSidePadding={router.pathname?.includes(ROUTES.MY_PV) ? 0 : 20}
        >
            <Title withMargin={!router.pathname.includes(ROUTES.MY_PV)}>{getTitle()}</Title>
            <Wrapper>
                {filterVersion === FilterViewTypes.DESKTOP &&
                    !hasSomeFilter &&
                    !hasSelectedFilters &&
                    showBanners &&
                    !isMyAccount && (
                        <DesktopFilters>
                            <Banner
                                onClick={() => router.push({ pathname: ROUTES.COMPANY_COURSES })}
                                src={publicAssets('/banner.webp')}
                            />
                            <Newsletter isStaticBanner />
                        </DesktopFilters>
                    )}
                {filterVersion === FilterViewTypes.DESKTOP && (hasSomeFilter || hasSelectedFilters) && (
                    <DesktopFilters>
                        <BorderedPart>
                            {conferencesFiltersCount && (
                                <FilterTemplate
                                    name={t('filters.conferenceType')}
                                    data={[]}
                                    conferenceTypeCounts={conferencesFiltersCount}
                                    type={FilterTypesInResponse.CONFERENCE_TYPE}
                                    withBorderBottom={!!trainingFiltersCount}
                                />
                            )}
                            {trainingFiltersCount && (
                                <FilterTemplate
                                    name={t('filters.courseType')}
                                    data={[]}
                                    courseTypeCounts={trainingFiltersCount}
                                    type={FilterTypesInResponse.TRAINING_TYPE}
                                    withBorderBottom={!!packageFilters}
                                />
                            )}
                            {packageFilters && (
                                <FilterTemplate
                                    name={t('filters.packageType')}
                                    data={packageFilters}
                                    type={FilterTypesInResponse.PACKAGES}
                                    withBorderBottom={!!categoryFilters}
                                />
                            )}
                            {categoryFilters?.length > 0 && (
                                <FilterTemplate
                                    name={t('filters.courseArea')}
                                    data={categoryFilters}
                                    type={FilterTypesInResponse.CATEGORY}
                                    withBorderBottom={monthFilters?.length > 0}
                                />
                            )}
                            {monthFilters?.length > 0 && (
                                <FilterTemplate
                                    name={t('filters.months')}
                                    data={monthFilters}
                                    type={FilterTypesInResponse.MONTH}
                                    withBorderBottom={!!personFilters}
                                />
                            )}
                            {personFilters && (
                                <FilterTemplate name={t('filters.lecturer')} data={personFilters} isLecturer />
                            )}
                        </BorderedPart>
                        {!isMyAccount && showBanners && (
                            <Banner
                                onClick={() => router.push({ pathname: ROUTES.COMPANY_COURSES })}
                                src={publicAssets('/banner.webp')}
                            />
                        )}
                        {!isMyAccount && showBanners && <Newsletter isStaticBanner />}
                    </DesktopFilters>
                )}
                {filterVersion === FilterViewTypes.MOBILE && hasSomeFilter && (
                    <MobileFilters className={`${isOpenFilter ? 'isOpen' : 'isClose'}`}>
                        <MobileFiltersTitle>{t('filters.title')}</MobileFiltersTitle>
                        <MobileCloseButton onClick={() => setIsOpenFilter(false)}>&#10005;</MobileCloseButton>
                        {conferencesFiltersCount && (
                            <FilterTemplate
                                name={t('filters.conferenceType')}
                                data={[]}
                                conferenceTypeCounts={conferencesFiltersCount}
                                type={FilterTypesInResponse.CONFERENCE_TYPE}
                                withBorderBottom
                            />
                        )}
                        {trainingFiltersCount && (
                            <FilterTemplate
                                name={t('filters.courseType')}
                                data={[]}
                                courseTypeCounts={trainingFiltersCount}
                                type={FilterTypesInResponse.TRAINING_TYPE}
                                withBorderBottom={!!packageFilters}
                            />
                        )}
                        {packageFilters && (
                            <FilterTemplate
                                name={t('filters.packageType')}
                                data={packageFilters}
                                type={FilterTypesInResponse.PACKAGES}
                                withBorderBottom={!!categoryFilters}
                            />
                        )}
                        {categoryFilters?.length > 0 && (
                            <FilterTemplate
                                name={t('filters.courseArea')}
                                data={categoryFilters}
                                type={FilterTypesInResponse.CATEGORY}
                                withBorderBottom={monthFilters?.length > 0}
                            />
                        )}
                        {monthFilters?.length > 0 && (
                            <FilterTemplate
                                name={t('filters.months')}
                                data={monthFilters}
                                type={FilterTypesInResponse.MONTH}
                                withBorderBottom={!!personFilters}
                            />
                        )}
                        {personFilters && (
                            <FilterTemplate
                                name={t('filters.lecturer')}
                                data={personFilters}
                                isLecturer
                                withBorderBottom
                            />
                        )}
                        <MobileResetSection>
                            <ButtonTemplate
                                onClick={() => {
                                    router.push({ pathname: router.pathname });
                                    setIsOpenFilter(false);
                                }}
                                text={t('filters.reset')}
                                marginTop={20}
                                marginBottom={20}
                                whiteType
                            />
                        </MobileResetSection>
                    </MobileFilters>
                )}
                <Trainings
                    withoutFilters={
                        (!trainingFiltersCount && !categoryFilters && !personFilters && !cityFilters && !showBanners) ||
                        (!conferencesFiltersCount && !personFilters && !showBanners)
                    }
                >
                    <Header>
                        {(trainings?.length > 0 || router.query?.search) && (
                            <SearchTemplate label={getSearchLabelByPage()} />
                        )}
                        {hasSomeFilter && (
                            <MobileFilterButton onClick={() => setIsOpenFilter(true)}>
                                {t('filters.edit')}
                            </MobileFilterButton>
                        )}
                    </Header>
                    {router?.query?.search && trainings?.length === 0 && (
                        <SearchNoResults>
                            {router.asPath?.includes(ROUTES.CONFERENCES)
                                ? t('conferences.empty.search', { value: router.query.search })
                                : t('trainings.empty.search', { value: router.query.search })}
                        </SearchNoResults>
                    )}
                    {hasSelectedFilters && !router?.query?.search && trainings?.length === 0 && (
                        <SearchNoResults withoutMargin>
                            {router.asPath?.includes(ROUTES.CONFERENCES)
                                ? t('conferences.empty.filters')
                                : t('trainings.empty.filters')}
                        </SearchNoResults>
                    )}
                    {trainings?.map((event: ITrainingItem, key) => {
                        const index = Math.floor(Math.random() * banners.length);

                        return (
                            <Item
                                key={`training-${
                                    event?.course?._key || event?.conference?._key || event?.event?._key
                                }-${key}-${event?.dates?.[0]?.date?._key}`}
                            >
                                {key === INTERNAL_BANNER_POSITION && banners?.length > 0 && (
                                    <a target="_blank" href={banners[index]?.redirect_url} rel="noreferrer">
                                        <InternalBanner
                                            src={`${getImageBaseURL()}/media/image/${bannerVersion}_${
                                                banners[index]?._key
                                            }/file`}
                                        />
                                    </a>
                                )}
                                {key === EXTERNAL_BANNER_POSITION && externalBannerScript && !isLoggedIn && (
                                    <ExternalBanner id="homepageBanner">
                                        <Script>{externalBannerScript}</Script>
                                    </ExternalBanner>
                                )}
                                <EventTemplate
                                    iKey={`${
                                        event?.course?._key || event?.conference?._key || event?.event?._key
                                    }-${key}-${event?.dates?.[0]?.date?._key}`}
                                    event={event}
                                />
                            </Item>
                        );
                    })}
                    {trainings.length > 0 &&
                        trainings.length < totalCount &&
                        (isLoading ? (
                            <SpinnerTemplate size={32} />
                        ) : (
                            <ReactPaginate
                                breakLabel="..."
                                onPageChange={showMore}
                                nextLabel=">"
                                previousLabel="<"
                                pageRangeDisplayed={
                                    router.pathname.includes(ROUTES.TRAININGS)
                                        ? TRAININGS_LIMIT_PER_PAGE
                                        : CONFERENCES_LIMIT_PER_PAGE
                                }
                                pageCount={
                                    totalCount /
                                    (router.pathname.includes(ROUTES.TRAININGS)
                                        ? TRAININGS_LIMIT_PER_PAGE
                                        : CONFERENCES_LIMIT_PER_PAGE)
                                }
                                renderOnZeroPageCount={null}
                                forcePage={Number(router.query.strana) - 1 || 0}
                                containerClassName="pagination"
                                activeClassName="active"
                            />
                        ))}
                </Trainings>
            </Wrapper>
        </ContentTemplate>
    );
};

export default TrainingsTemplate;
